import * as ko from "knockout";

import pliCall, { PliCallPromise, PliCallResponse, PliCallErrorData } from "../core/call";
import * as pliObservable from "../forms-knockoutcontrols";

import * as shared from "./signInShared";
import { SignInPageMain } from "./signInPageMain";


/**
 * Settings for "SignInPageForgot.activate" calls.
 */
export interface SignInPageForgotActivationSettings {
    /**
     * How many milliseconds after activation should the initial focus be set.
     * If not specified, defaults to 500 milliseconds (default page transition animation duration).
     * If <= 0, focus is set instantly.
     */
    focusDelay?: number;

    /** Flag indicating whether the user is requesting password reset (true) or only username retrieval (false). */
    resetPassword?: boolean;

    /**
     * Server response that caused forgot-page to be activated.
     * Stored in "serverData" observable which is used by view to allow
     * server to override built-in texts and links.
     */
    serverData?: any;

    /**
     * Initial username to set (useful when transitioning from sign-in
     * page and the user has already entered the username/email).
     */
    username?: string;
}


/**
 * Viewmodel for reset-password page.
 */
export class SignInPageForgot {

    constructor(owner: shared.SignInPagesCollection, pageId: shared.SignInPageId) {
        this._owner = owner;
        this._pageId = pageId;
    }


    // -------------------------------------------------------------------------
    // Viewmodel properties
    // -------------------------------------------------------------------------

    /** Current server call or null (reset-password request). */
    call: ko.Observable<PliCallPromise<any, SignInPageForgot>> = ko.observable(null);

    /** Password-reset completion status (will be set to "true" when server call succeeds). */
    done: ko.Observable<boolean> = ko.observable(false);

    /** Tracks email entered by user. */
    email: shared.TextBoxObservableWithFocus<string> =
        shared.createTextBoxObservableWithFocus("", null, pliObservable.behaviours.required);

    /** Error message currently being displayed. */
    error: ko.Observable<string> = ko.observable("");

    /** Was password reset (true) or only username retrieval (false) requested in "activate" method? */
    resetPassword: ko.Observable<boolean> = ko.observable(false);

    /** Server response that caused forgot-page to be activated. */
    serverData: ko.Observable<any> = ko.observable(shared.emptyData);

    /** Flag that tracks focus of the submit button. */
    submitFocus: ko.Observable<boolean> = ko.observable(false);

    /** Tracks username entered by user. */
    username: shared.TextBoxObservableWithFocus<string> =
        shared.createTextBoxObservableWithFocus("", null, pliObservable.behaviours.required);


    // -------------------------------------------------------------------------
    // Viewmodel actions
    // -------------------------------------------------------------------------

    /**
     * Transitions to this page + set initial focus.
     */
    activate(settings?: SignInPageForgotActivationSettings): void {
        this.call(null);
        this.done(false);
        this.error("");
        this.resetPassword(!!(settings && settings.resetPassword));
        this.serverData((settings && settings.serverData) || shared.emptyData);

        const initialEmail = (this.serverData().passwordResetRequiresUsername ? "" : (settings && settings.username || ""));
        this.email.reset(false, initialEmail, initialEmail);
        const initialUsername = (this.serverData().passwordResetRequiresUsername ? (settings && settings.username || "") : "");
        this.username.reset(false, initialUsername, initialUsername);

        // initiate page transition
        this._owner.currentPageId(this._pageId);

        // set initial focus
        let focusDelay = (settings && settings.focusDelay);
        if (typeof focusDelay !== "number") { focusDelay = 500; }
        const focusField = (this.resetPassword() && this.serverData().passwordResetRequiresUsername ? this.username : this.email);
        if (focusDelay > 0) {
            setTimeout(() => focusField.hasFocus(true), focusDelay);
        } else {
            focusField.hasFocus(true);
        }
    }

    /**
     * Transition back to main page.
     */
    cancel(): void {
        if (this.call()) { return; }

        const mainPage = this._owner.getPage<SignInPageMain>(shared.SignInPageId.Main);
        mainPage.activate({ transition: -1 });
    }

    /**
     * Validate email + initiate username-retrieval/password-reset server call.
     */
    submit(): void {
        if (this.call()) { return; }
        if (this.done()) {
            const mainPage = this._owner.getPage<SignInPageMain>(shared.SignInPageId.Main);
            mainPage.activate({ transition: -1 });
            return;
        }

        if (this.serverData().passwordResetRequiresUsername) {
            if (this.resetPassword()) {
                this.username.validate();
            } else {
                this.username.setErrorMessage(null);
            }
            this.email.validate();
        } else {
            this.username.setErrorMessage(null);
            this.email.validate();
        }

        let errorMessage: string = null;
        if (!this.username.isValid() && !this.email.isValid()) {
            errorMessage = "Missing username and email";
            this.username.hasFocus();
        } else if (!this.username.isValid()) {
            errorMessage = "Missing username";
            this.username.hasFocus();
        } else if (!this.email.isValid()) {
            errorMessage = "Missing email";
            this.email.hasFocus();
        }
        if (errorMessage) {
            this.error(errorMessage + ".\nPlease try again.");
            return;
        }

        this.error("");
        if (this.resetPassword()) {
            this.call(pliCall({
                owner: this,
                name: "Pli-Authentication-ResetPassword",
                data: { username: this.username(), email: this.email() }
            }));
        } else {
            this.call(pliCall({
                owner: this,
                name: "Pli-Authentication-RetrieveUsername",
                data: { email: this.email() }
            }));
        }
        this.call().always(SignInPageForgot._processResponse);
    }


    // -------------------------------------------------------------------------
    // Private
    // -------------------------------------------------------------------------

    /**
     * Processes result of username-retrieval/password-reset server call.
     */
    private static _processResponse(response: PliCallResponse<any, SignInPageForgot>): void {
        if (response.owner.call().xhr !== response.clientXhr) {
            console.warn("Ignoring response ", response);
            return;
        }

        let focusObservable: ko.Observable<boolean> = null;
        if (response.isError) {
            const errorData = response.result as PliCallErrorData;
            response.owner.error(errorData.message + ".\nPlease try again.");
            if (errorData.validationErrors) {
                let message: string;
                message = errorData.validationErrors["email"];
                if (message) { response.owner.email.setErrorMessage(message); }
            }
            focusObservable = (response.owner.resetPassword() && response.owner.serverData().passwordResetRequiresUsername
                ? response.owner.username.hasFocus
                : response.owner.email.hasFocus);
        } else {
            response.owner.done(true);
            focusObservable = response.owner.submitFocus;
        }

        response.owner.call(null);
        focusObservable(true);
    }

    /** Collection that owns this page. Used for page transitions. */
    private _owner: shared.SignInPagesCollection;

    /** ID of the page that was instantiated with this viewmodel instance. */
    private _pageId: shared.SignInPageId; // readonly, initialized in constructor

}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/forms-signin/signInPageForgot.ts